import { CalendarDate, now, parseZonedDateTime } from '@internationalized/date';
import { produce } from 'immer';
import _ from 'lodash';
import {
  useState,
  createContext,
  ReactNode,
  useContext,
  ReactElement,
  useEffect,
} from 'react';
import { getLiveSlots } from '@provider-directory/app/actions/get-slots';
import { FullProviderAvailability } from '@provider-directory/components/availability/availability.model';
import { useProvider } from '@provider-directory/contexts/ProviderContext';
import { ProviderAvailabilityResponse } from '@provider-directory/types/providers.contracts';
import {
  ProviderTimeSlot,
  ReasonForVisitSlotData,
} from '@provider-directory/types/scheduling.contracts';

const mapEpicResponseToSlotsV2 = (
  schedule: ProviderAvailabilityResponse['schedule'],
): FullProviderAvailability => {
  const newSlots: FullProviderAvailability = {};

  schedule.forEach(({ departmentId, days }) => {
    const departmentKey = departmentId.replace('External|', '');

    days.forEach(({ date, slots }) => {
      const validSlots: ProviderTimeSlot[] = [];

      slots.forEach((slot) => {
        // Filter out slots that are in the past or less than 60 minutes away
        const slotDateTime = parseZonedDateTime(
          `${date}T${slot.startTime}[America/New_York]`,
        );
        if (slotDateTime < now('America/New_York').add({ minutes: 60 })) {
          return;
        }

        // Create reasonsForVisit map
        const reasonsForVisitMap: Record<string, ReasonForVisitSlotData> = {};
        slot.reasonsForVisit.forEach((reasonForVisitData) => {
          const rfvId = reasonForVisitData.id;
          if (rfvId) {
            reasonsForVisitMap[rfvId] = {
              id: rfvId,
              visitTypeId: reasonForVisitData.visitTypeId,
            };
          }
        });

        const formattedSlot: ProviderTimeSlot = {
          date,
          start: slot.startTime,
          timeZone: slot.timeZone,
          reasonsForVisit: reasonsForVisitMap,
        };

        validSlots.push(formattedSlot);
      });

      if (validSlots.length > 0) {
        // Initialize nested structure
        const deptSlots = (newSlots[departmentKey] ??= {});
        const dateSlots = (deptSlots[date] ??= []);

        dateSlots.push(...validSlots);
      }
    });
  });

  return newSlots;
};

interface FetchedSlotsContextValue {
  isLoading: boolean;
  keyedSlots: FullProviderAvailability;
  fetchSlotsForDay?: (date: CalendarDate) => void;
}

const FetchedSlotsContext = createContext<FetchedSlotsContextValue>({
  isLoading: true,
  keyedSlots: {} as FullProviderAvailability,
});

export const LiveSlotsProvider = ({
  children,
  reasonForVisit,
}: {
  children: ReactNode;
  reasonForVisit: string;
}): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [keyedSlots, setKeyedSlots] = useState<
    FetchedSlotsContextValue['keyedSlots']
  >({} as FullProviderAvailability);
  const [fetchedDays, setFetchedDays] = useState<Set<string>>(new Set());
  const { provider } = useProvider();
  const addFetchedDay = (date: CalendarDate) => {
    setFetchedDays((prev) => new Set([...prev, date.toString()]));
  };

  const hasFetchedDay = (date: CalendarDate) =>
    fetchedDays.has(date.toString());

  const fetchDateRange = async (
    startDate: CalendarDate,
    endDate: CalendarDate,
  ): Promise<ProviderAvailabilityResponse | null> => {
    if (!provider || !reasonForVisit) {
      return null;
    }

    const response = await getLiveSlots({
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      providerId: `NPI|${provider.npi}`,
      departmentIds: provider.locations
        .map(({ ehrExternalId }) => ehrExternalId ?? '')
        .filter(Boolean),
      reasonForVisit,
    });
    return response;
  };

  const addEpicResponseToSlots = ({
    schedule,
  }: ProviderAvailabilityResponse) => {
    const newSlots = mapEpicResponseToSlotsV2(schedule);

    setKeyedSlots(
      produce((draft) => {
        _.merge(draft, newSlots);
      }),
    );
  };

  const fetchSlotsForDay = async (date: CalendarDate) => {
    if (hasFetchedDay(date) || !provider) {
      return;
    }

    addFetchedDay(date);
    setIsLoading(true);
    const response = await fetchDateRange(date, date.add({ days: 1 }));

    if (response) {
      addEpicResponseToSlots(response);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (provider?.npi) {
      setFetchedDays(new Set());
      setKeyedSlots({} as FullProviderAvailability);
    }
  }, [provider?.npi]);

  return (
    <FetchedSlotsContext.Provider
      value={{
        isLoading,
        keyedSlots,
        fetchSlotsForDay,
      }}
    >
      {children}
    </FetchedSlotsContext.Provider>
  );
};

export const useLiveSlots = (): FetchedSlotsContextValue => {
  return useContext(FetchedSlotsContext);
};
